import { motion } from "framer-motion";
import { Play, ArrowUpRight } from "lucide-react";
import { SiYoutube, SiInstagram, SiTiktok } from "react-icons/si";
import { VIDEOS, SITE } from "@/data/site";

const socials = [
  { key: "youtube", href: SITE.socials.youtube, Icon: SiYoutube, label: "YouTube", handle: "@nancy_drums" },
  { key: "instagram", href: SITE.socials.instagram, Icon: SiInstagram, label: "Instagram", handle: "@nancy_drums" },
  { key: "tiktok", href: SITE.socials.tiktok, Icon: SiTiktok, label: "TikTok", handle: "@nancy_drums" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06 } }),
};

const VideoCard = ({ video, index }) => (
  <motion.div
    custom={index}
    variants={fadeUp}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, amount: 0.2 }}
    className="group"
    data-testid={`video-${index}`}
  >
    {video.type === "embed" ? (
      <div className="overflow-hidden rounded-3xl border border-teal/30 bg-ink aspect-video">
        <iframe
          className="w-full h-full"
          src={`https://www.youtube.com/embed/${video.youtubeId}${video.start ? `?start=${video.start}` : ""}`}
          title={video.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          data-testid={`video-embed-${index}`}
        />
      </div>
    ) : (
      <a
        href={video.url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`video-thumb-${index}`}
        className="block relative overflow-hidden rounded-3xl border border-teal/30 aspect-video"
      >
        <img
          src={video.thumbnail}
          alt={video.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-ink/40 group-hover:bg-ink/25 transition-colors duration-300" />
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="h-16 w-16 rounded-full bg-cream/90 text-ink flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
            <Play size={26} fill="currentColor" />
          </span>
        </span>
      </a>
    )}
  </motion.div>
);

export default function Portfolio({ embedded = false }) {
  return (
    <div data-testid="portfolio-page" className={embedded ? "" : "pt-16"}>
      {/* Header */}
      <section className="bg-cream text-ink pt-20 md:pt-28 pb-8">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter leading-none">
            Videos
          </h1>
        </div>
      </section>

      {/* Video grid */}
      <section className="bg-cream pt-0 pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="video-grid">
            {VIDEOS.map((v, i) => (
              <VideoCard key={i} video={v} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Socials */}
      <section id="socials" className="bg-sage py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <h2 className="font-display text-5xl md:text-7xl font-black tracking-tighter leading-none text-ink mb-12">
            Socials
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14" data-testid="social-grid">
            {socials.map(({ key, href, Icon, label, handle }) => (
              <a
                key={key}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`portfolio-social-${key}`}
                className="group rounded-3xl bg-cream border border-teal/30 p-8 flex items-center justify-between transition-all duration-300 hover:bg-ink hover:border-ink"
              >
                <div className="flex items-center gap-4">
                  <span className="h-12 w-12 rounded-full bg-terracotta text-ink flex items-center justify-center group-hover:bg-cream group-hover:text-ink transition-colors duration-300">
                    <Icon size={22} />
                  </span>
                  <div>
                    <p className="font-display text-xl text-ink group-hover:text-cream transition-colors duration-300">{label}</p>
                    <p className="text-sm text-steel group-hover:text-cream/70 transition-colors duration-300">{handle}</p>
                  </div>
                </div>
                <ArrowUpRight className="text-steel group-hover:text-cream transition-colors duration-300" />
              </a>
            ))}
          </div>

          <div className="columns-2 md:columns-3 lg:columns-4 gap-4 md:gap-6" data-testid="socials-gallery">
            {[
              "/socials/s1.webp",
              "/socials/s3.webp",
              "/socials/s4.webp",
              "/socials/s2.webp",
            ].map((src, i) => (
              <div
                key={src}
                className="mb-4 md:mb-6 overflow-hidden rounded-3xl border border-teal/30 break-inside-avoid"
                data-testid={`social-photo-${i}`}
              >
                <img
                  src={src}
                  alt={`Nancy Naden ${i + 1}`}
                  loading="lazy"
                  className="w-full h-auto object-cover transition-transform duration-500 hover:scale-105"
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
